import { useCallback, useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import settings from '../../settings.js'

import { isValidArray, isValidString, parseJson } from 'mmfintech-commons'
import {
  CheckoutFlowStep,
  PaymentMethods,
  useCheckoutPayment,
  useLanguageParam,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'

import { DepositResult } from '../DepositResult'
import { CheckoutVoucher } from './Voucher'
import { BlixcardVoucher } from './Blixcard'
import { CustomPreloader, withCheckoutContainer } from '@components'
import type { CheckoutMethodField } from 'mmfintech-checkout-commons/types'

const WrappedCheckoutBlixcard = withCheckoutContainer(BlixcardVoucher, {
  showLanguageMenu: true,
  showHeader: true
})

const WrappedCheckoutVoucher = withCheckoutContainer(CheckoutVoucher, {
  showLanguageMenu: true,
  showHeader: true
})

const DepositResultWrapped = withCheckoutContainer(DepositResult, {
  showLanguageMenu: true,
  showHeader: true
})

const Checkout = () => {
  useLanguageParam(settings.languages)
  const sessionId = useSessionId()

  const { checkoutPay, checkoutPayFetching, session } = useSelector(
    ({ checkout: { checkoutPay, checkoutPayFetching, session } }) => ({
      checkoutPay,
      checkoutPayFetching,
      session
    }),
    shallowEqual
  )

  useUnloadBeacon(sessionId)

  const triggeredRef = useRef<boolean>(false)

  const {
    step,
    // setStep,
    fieldValues,
    countryCode,
    setCountryCode,
    hasOnlyOneCountry,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentOption,
    handlePay,
    initialized,
    showFieldsAfterBanks
  } = useCheckoutPayment({
    initialStep: CheckoutFlowStep.SELECT_METHOD,
    preselectMethod:
      settings.blixcard || ((!session?.brand || session?.brand === 'JETON_BANK') && settings.brand === 'MIKADO')
        ? ''
        : PaymentMethods.INSTANT_BANK_TRANSFER,
    preselectSingleBank: true,
    preselectSingleMethod: true
  })

  const prepareResponse = useCallback(() => {
    if (session?.sessionStatus?.value === 'IN_PROGRESS') {
      return {
        result: parseJson(session?.paymentResult, session?.paymentResult),
        resultType: session?.paymentResultType,
        processingAmount: session?.processingAmount,
        processingCurrency: session?.processingCurrency
      }
    }
    return checkoutPay
  }, [checkoutPay, session])

  const validFieldsStep = useMemo(() => {
    if (isValidArray(selectedPaymentOption?.fields)) {
      return !selectedPaymentOption.fields.some(
        (field: CheckoutMethodField) =>
          (!field.prefilledValue || field.displayIfPrefilled) && field.name !== 'bankChoiceId'
      )
    }
    return true
  }, [selectedPaymentOption])

  const checkAndExecute = () => {
    // console.log('————————————————————————————————————————')
    // console.log('Step:', step)
    // console.log('Initialized:', initialized)
    // console.log('Triggered:', triggeredRef.current)
    // console.log('Payment method:', isValidString(paymentMethod))
    // console.log('Fields:', validFieldsStep)
    if (
      initialized &&
      !triggeredRef.current &&
      !checkoutPayFetching &&
      isValidString(paymentMethod) &&
      !fieldValues.hasBankSelection &&
      validFieldsStep
    ) {
      triggeredRef.current = true
      handlePay()
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger if paymentMethod === 'JETON'
  useEffect(() => {
    if (
      session?.brand === 'JETON_BANK' &&
      settings.brand === 'MIKADO' &&
      paymentMethod === PaymentMethods.JETON &&
      hasOnlyOneCountry
    ) {
      checkAndExecute()
    }
  }, [paymentMethod])

  if (!initialized) {
    return <CustomPreloader />
  }

  if (
    step === CheckoutFlowStep.ADDITIONAL_FIELDS ||
    step === CheckoutFlowStep.SELECT_METHOD ||
    step === CheckoutFlowStep.SELECT_BANK
  ) {
    return settings.blixcard ? (
      <WrappedCheckoutBlixcard
        sessionId={sessionId}
        fieldValues={fieldValues}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        handlePay={handlePay}
      />
    ) : (
      <WrappedCheckoutVoucher
        sessionId={sessionId}
        fieldValues={fieldValues}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        handlePay={handlePay}
        showFieldsAfterBanks={showFieldsAfterBanks}
      />
    )
  }

  if (step === CheckoutFlowStep.DEPOSIT_RESULT) {
    return <DepositResultWrapped response={prepareResponse()} session={session} sessionId={sessionId} />
  }

  return null
}

export default Checkout
